<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWaCampaigns } from 'dashboard/composables/useWaCampaigns';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

import CampaignCard from './components/CampaignCard.vue';
import CampaignForm from './components/CampaignForm.vue';
import CampaignLogs from './components/CampaignLogs.vue';
import ExecutionCalendar from './components/ExecutionCalendar.vue';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const {
  campaigns,
  stats,
  loading,
  saving,
  fetchCampaigns,
  fetchStats,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  pauseCampaign,
  resumeCampaign,
  fetchLogs,
} = useWaCampaigns();

const searchQuery = ref('');
const filterStatus = ref('all');
const showFormModal = ref(false);
const editingCampaign = ref(null);
const showLogsModal = ref(false);
const logsCampaign = ref(null);

onMounted(async () => {
  await Promise.all([fetchCampaigns(), fetchStats()]);
});

const filteredCampaigns = computed(() => {
  let result = campaigns.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(c => c.title.toLowerCase().includes(query));
  }

  if (filterStatus.value !== 'all') {
    result = result.filter(c => c.status === filterStatus.value);
  }

  return result;
});

const dailyLimitPercent = computed(() => {
  const limit = stats.value.daily_limit || 50;
  const sent = stats.value.sent_today || 0;
  return Math.min((sent / limit) * 100, 100);
});

const openCreateForm = () => {
  editingCampaign.value = null;
  showFormModal.value = true;
};

const openEditForm = campaign => {
  editingCampaign.value = campaign;
  showFormModal.value = true;
};

const handleSave = async data => {
  if (editingCampaign.value) {
    await updateCampaign(editingCampaign.value.id, data);
  } else {
    await createCampaign(data);
  }
  showFormModal.value = false;
  editingCampaign.value = null;
  await fetchStats();
};

const handleDelete = async campaignId => {
  if (window.confirm(t('OMNI.WA_CAMPAIGNS.CONFIRM_DELETE_MESSAGE', { name: '' }))) {
    await deleteCampaign(campaignId);
    await fetchStats();
  }
};

const handlePause = async campaignId => {
  await pauseCampaign(campaignId);
  await fetchStats();
};

const handleResume = async campaignId => {
  await resumeCampaign(campaignId);
  await fetchStats();
};

const openLogs = campaign => {
  logsCampaign.value = campaign;
  showLogsModal.value = true;
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0 bg-n-background">
    <!-- Header -->
    <header class="bg-n-solid-2 border-b border-n-weak px-8 py-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">
            {{ t('OMNI.WA_CAMPAIGNS.TITLE') }}
          </h1>
          <p class="text-n-slate-11 mt-1">
            {{ t('OMNI.WA_CAMPAIGNS.DESCRIPTION') }}
          </p>
        </div>
        <button
          v-if="canEditSection('wa_campaigns')"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-woot-500 text-white rounded-lg hover:bg-woot-600 font-medium shadow-sm"
          @click="openCreateForm"
        >
          <span class="i-lucide-plus text-lg" />
          {{ t('OMNI.WA_CAMPAIGNS.NEW_CAMPAIGN') }}
        </button>
      </div>

      <!-- Stats Bar -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <!-- Active Campaigns -->
        <div class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-800/50 flex items-center justify-center">
              <span class="i-lucide-megaphone text-xl text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-green-700 dark:text-green-300">
                {{ stats.active_campaigns }}
              </div>
              <div class="text-xs text-green-600/70 dark:text-green-400/70">
                {{ t('OMNI.WA_CAMPAIGNS.STATS.ACTIVE') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Paused Campaigns -->
        <div class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-800/50 flex items-center justify-center">
              <span class="i-lucide-pause text-xl text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                {{ stats.paused_campaigns }}
              </div>
              <div class="text-xs text-yellow-600/70 dark:text-yellow-400/70">
                {{ t('OMNI.WA_CAMPAIGNS.STATS.PAUSED') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Sent Today / Daily Limit -->
        <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center">
              <span class="i-lucide-send text-xl text-blue-600 dark:text-blue-400" />
            </div>
            <div class="flex-1">
              <div class="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {{ stats.sent_today }} / {{ stats.daily_limit }}
              </div>
              <div class="text-xs text-blue-600/70 dark:text-blue-400/70">
                {{ t('OMNI.WA_CAMPAIGNS.STATS.SENT_TODAY') }}
              </div>
              <div class="mt-1 h-1.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all"
                  :class="dailyLimitPercent >= 90 ? 'bg-red-500' : 'bg-blue-500'"
                  :style="{ width: `${dailyLimitPercent}%` }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Sent This Week -->
        <div class="bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-800/50 flex items-center justify-center">
              <span class="i-lucide-calendar-days text-xl text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-violet-700 dark:text-violet-300">
                {{ stats.sent_this_week }}
              </div>
              <div class="text-xs text-violet-600/70 dark:text-violet-400/70">
                {{ t('OMNI.WA_CAMPAIGNS.STATS.SENT_THIS_WEEK') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Filters -->
    <div class="bg-n-solid-2 border-b border-n-weak px-8 py-4">
      <div class="flex items-center gap-4">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('OMNI.WA_CAMPAIGNS.SEARCH_PLACEHOLDER')"
          class="flex-1 min-w-0 px-2 py-1.5 text-xs border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-shadow"
        />
        <select
          v-model="filterStatus"
          class="w-36 px-4 py-3 border border-n-weak rounded-xl bg-n-alpha-3 text-n-slate-12"
        >
          <option value="all">
            {{ t('OMNI.WA_CAMPAIGNS.FILTER.ALL') }}
          </option>
          <option value="active">
            {{ t('OMNI.WA_CAMPAIGNS.FILTER.ACTIVE') }}
          </option>
          <option value="paused">
            {{ t('OMNI.WA_CAMPAIGNS.FILTER.PAUSED') }}
          </option>
          <option value="completed">
            {{ t('OMNI.WA_CAMPAIGNS.FILTER.COMPLETED') }}
          </option>
        </select>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto px-8 py-6">
      <div v-if="loading" class="flex items-center justify-center h-64">
        <span class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500" />
      </div>

      <div v-else-if="filteredCampaigns.length === 0" class="text-center py-16">
        <span class="i-lucide-megaphone text-6xl text-n-slate-8 mb-4 block mx-auto" />
        <p class="text-n-slate-11">
          {{ t('OMNI.WA_CAMPAIGNS.NO_CAMPAIGNS') }}
        </p>
        <button
          v-if="canEditSection('wa_campaigns')"
          class="mt-4 text-woot-500 hover:text-woot-600 font-medium"
          @click="openCreateForm"
        >
          {{ t('OMNI.WA_CAMPAIGNS.ADD_FIRST_CAMPAIGN') }}
        </button>
      </div>

      <template v-else>
        <!-- Execution Calendar -->
        <ExecutionCalendar
          :campaigns="campaigns"
          class="mb-6"
        />

        <!-- Campaign Cards -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <CampaignCard
            v-for="campaign in filteredCampaigns"
            :key="campaign.id"
            :campaign="campaign"
            @edit="openEditForm(campaign)"
            @delete="handleDelete(campaign.id)"
            @pause="handlePause(campaign.id)"
            @resume="handleResume(campaign.id)"
            @view-logs="openLogs(campaign)"
          />
        </div>
      </template>
    </div>

    <!-- Form Modal -->
    <CampaignForm
      :is-open="showFormModal"
      :campaign="editingCampaign"
      :saving="saving"
      @save="handleSave"
      @close="showFormModal = false"
    />

    <!-- Logs Modal -->
    <CampaignLogs
      :is-open="showLogsModal"
      :campaign="logsCampaign"
      :fetch-logs="fetchLogs"
      @close="showLogsModal = false"
    />
  </div>
</template>
